import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Badge, Alert } from 'reactstrap';
import { getNode, processUserChoice, shouldEscalate, getConversationSummary, getCurrentLanguage } from '../ai/technicalSupportDecisionTree';
import { API_ROOT } from '../config';
import '../styles/TechnicalSupportChat.css';

const TechnicalSupportChat = ({ onClose, machineId = null, isModal = true, chatId = null, hideHeader = false }) => {
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentNodeId, setCurrentNodeId] = useState('start');
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [incidentCreated, setIncidentCreated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('es');
  const [isMobile, setIsMobile] = useState(false);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect language changes
  useEffect(() => {
    const detectLanguage = () => {
      const lang = getCurrentLanguage();
      setCurrentLanguage(lang);
    };

    detectLanguage();
    
    // Listen for language changes (Google Translate events)
    const interval = setInterval(detectLanguage, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Initialize chat with welcome message - reset when chatId changes
  useEffect(() => {
    const startNode = getNode('start', currentLanguage);
    setConversationHistory([
      {
        id: Date.now(),
        message: startNode.message,
        options: startNode.options,
        isBot: true,
        timestamp: new Date()
      }
    ]);
    setCurrentNodeId('start');
    setUserInput('');
    setIncidentCreated(false);
  }, [currentLanguage, chatId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  const handleOptionClick = async (optionIndex) => {
    const currentNode = getNode(currentNodeId, currentLanguage);
    const selectedOption = currentNode.options[optionIndex];

    // Check if this option requires text input
    if (selectedOption.requiresInput && !userInput.trim()) {
      const alertMessages = {
        es: 'Por favor, ingresa la información solicitada.',
        en: 'Please enter the requested information.',
        fr: 'Veuillez saisir les informations demandées.',
        de: 'Bitte geben Sie die angeforderten Informationen ein.',
        it: 'Si prega di inserire le informazioni richieste.',
        pt: 'Por favor, insira as informações solicitadas.'
      };
      alert(alertMessages[currentLanguage] || alertMessages['es']);
      return;
    }

    setIsLoading(true);

    // Add user's response to conversation
    const userMessage = {
      id: Date.now(),
      message: selectedOption.text,
      additionalInfo: userInput.trim() || null,
      isBot: false,
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, userMessage]);

    // Process the choice and get next node
    const result = processUserChoice(currentNodeId, optionIndex, userInput, currentLanguage);
    
    if (!result) {
      setIsLoading(false);
      return;
    }

    // Small delay for natural conversation feel
    await new Promise(resolve => setTimeout(resolve, 800));

    const nextNode = getNode(result.nextNodeId, currentLanguage);
    
    // Check if we need to escalate to incident
    if (shouldEscalate(result.nextNodeId)) {
      await handleEscalation(nextNode);
    }

    // Add bot's response to conversation
    const botMessage = {
      id: Date.now() + 1,
      message: nextNode.message,
      options: nextNode.options,
      isBot: true,
      isEndNode: nextNode.isEndNode,
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, botMessage]);
    setCurrentNodeId(result.nextNodeId);
    setUserInput('');
    setIsLoading(false);
  };

  const handleEscalation = async (node) => {
    try {
      const token = localStorage.getItem('token') || null;
      const summary = getConversationSummary(conversationHistory);

      const ticketData = {
        Machine: machineId || undefined,
        Category: 'OTHER',
        Priority: 'HIGH',
        Incidence: summary || 'Escalación desde chat de soporte técnico',
        ChatResumeLog: JSON.stringify(conversationHistory.map(m => ({
          message: m.message,
          isBot: m.isBot,
          timestamp: m.timestamp,
          additionalInfo: m.additionalInfo || undefined
        }))),
        OpenAt: new Date()
      };

      const config = {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(ticketData)
      };

      const response = await fetch(`${API_ROOT}supporttickets`, config);

      if (response.ok) {
        setIncidentCreated(true);
      } else {
        console.error('Failed to create support ticket:', response.statusText);
        setIncidentCreated(true); // Still show success to user, log error
      }
    } catch (error) {
      console.error('Error creating support ticket:', error);
      setIncidentCreated(true); // Still show the UI feedback
    }
  };

  const handleRestart = () => {
    setConversationHistory([]);
    setCurrentNodeId('start');
    setUserInput('');
    setIncidentCreated(false);
    
    const startNode = getNode('start', currentLanguage);
    setConversationHistory([
      {
        id: Date.now(),
        message: startNode.message,
        options: startNode.options,
        isBot: true,
        timestamp: new Date()
      }
    ]);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(currentLanguage === 'es' ? 'es-ES' : currentLanguage === 'en' ? 'en-US' : currentLanguage, { hour: '2-digit', minute: '2-digit' });
  };

  // UI translations
  const uiLabels = {
    es: {
      incidentCreated: 'Incidencia creada exitosamente. Un técnico se pondrá en contacto pronto.',
      enterResponse: 'Escribe tu respuesta aquí...',
      newConsultation: 'Nueva Consulta',
      close: 'Cerrar',
      technicalSupport: 'Soporte Técnico VACWAY',
      autoAssistant: 'Asistente automático de diagnóstico',
      machine: 'Máquina'
    },
    en: {
      incidentCreated: 'Incident created successfully. A technician will contact you soon.',
      enterResponse: 'Type your response here...',
      newConsultation: 'New Consultation',
      close: 'Close',
      technicalSupport: 'VACWAY Technical Support',
      autoAssistant: 'Automatic diagnostic assistant',
      machine: 'Machine'
    },
    fr: {
      incidentCreated: 'Incident créé avec succès. Un technicien vous contactera bientôt.',
      enterResponse: 'Tapez votre réponse ici...',
      newConsultation: 'Nouvelle consultation',
      close: 'Fermer',
      technicalSupport: 'Support technique VACWAY',
      autoAssistant: 'Assistant de diagnostic automatique',
      machine: 'Machine'
    },
    de: {
      incidentCreated: 'Vorfall erfolgreich erstellt. Ein Techniker wird sich bald bei Ihnen melden.',
      enterResponse: 'Geben Sie hier Ihre Antwort ein...',
      newConsultation: 'Neue Beratung',
      close: 'Schließen',
      technicalSupport: 'VACWAY Technischer Support',
      autoAssistant: 'Automatischer Diagnoseassistent',
      machine: 'Maschine'
    },
    it: {
      incidentCreated: 'Incidente creato con successo. Un tecnico ti contatterà presto.',
      enterResponse: 'Scrivi la tua risposta qui...',
      newConsultation: 'Nuova consultazione',
      close: 'Chiudi',
      technicalSupport: 'Supporto tecnico VACWAY',
      autoAssistant: 'Assistente diagnostico automatico',
      machine: 'Macchina'
    },
    pt: {
      incidentCreated: 'Incidente criado com sucesso. Um técnico entrará em contato em breve.',
      enterResponse: 'Digite sua resposta aqui...',
      newConsultation: 'Nova consulta',
      close: 'Fechar',
      technicalSupport: 'Suporte técnico VACWAY',
      autoAssistant: 'Assistente de diagnóstico automático',
      machine: 'Máquina'
    }
  };

  const labels = uiLabels[currentLanguage] || uiLabels['es'];

  // Memoize current node to prevent unnecessary recalculations
  const currentNode = useMemo(() => {
    return getNode(currentNodeId, currentLanguage);
  }, [currentNodeId, currentLanguage]);

  const requiresInput = useMemo(() => {
    return currentNode.options?.some(opt => opt.requiresInput) || false;
  }, [currentNode]);

  const ChatContent = () => (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      <div 
        ref={chatContainerRef}
        className="technical-support-chat-messages"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa'
        }}
      >
        {conversationHistory.map((item) => (
          <div
            key={item.id}
            className={`message-wrapper ${item.isBot ? 'bot-message' : 'user-message'}`}
            style={{
              display: 'flex',
              justifyContent: item.isBot ? 'flex-start' : 'flex-end',
              marginBottom: '1rem'
            }}
          >
            <div
              style={{
                maxWidth: '75%',
                backgroundColor: item.isBot ? '#ffffff' : '#007bff',
                color: item.isBot ? '#333' : '#ffffff',
                padding: '0.75rem 1rem',
                borderRadius: item.isBot ? '0.5rem 0.5rem 0.5rem 0' : '0.5rem 0.5rem 0 0.5rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}
            >
              <div>{item.message}</div>
              {item.additionalInfo && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  fontSize: '0.9em',
                  opacity: 0.9,
                  fontStyle: 'italic'
                }}>
                  {currentLanguage === 'es' ? 'Información adicional' : 
                   currentLanguage === 'en' ? 'Additional information' :
                   currentLanguage === 'fr' ? 'Informations supplémentaires' :
                   currentLanguage === 'de' ? 'Zusätzliche Informationen' :
                   currentLanguage === 'it' ? 'Informazioni aggiuntive' :
                   currentLanguage === 'pt' ? 'Informação adicional' :
                   'Información adicional'}: {item.additionalInfo}
                </div>
              )}
              <div style={{ 
                fontSize: '0.7em', 
                marginTop: '0.25rem', 
                opacity: 0.7,
                textAlign: 'right'
              }}>
                {formatTime(item.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {incidentCreated && (
          <Alert color="success" style={{ margin: '1rem 0' }}>
            <i className="fa fa-check-circle mr-2"></i>
            {labels.incidentCreated}
          </Alert>
        )}

        {isLoading && (
          <div className="bot-message" style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              backgroundColor: '#ffffff',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              <span className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      <div className="technical-support-chat-input" style={{ 
        padding: '1.25rem 1.5rem',
        backgroundColor: '#ffffff',
        borderTop: '2px solid #e9ecef',
        flexShrink: 0,
        maxHeight: '40vh',
        overflowY: 'auto'
      }}>
        {conversationHistory.length > 0 && 
         !conversationHistory[conversationHistory.length - 1].isEndNode && 
         conversationHistory[conversationHistory.length - 1].options?.length > 0 && (
          <>
            {/* Check if current node requires input */}
            {requiresInput && (
              <Input
                type="textarea"
                placeholder={labels.enterResponse}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                style={{ marginBottom: '1rem' }}
                rows="2"
                autoFocus
              />
            )}
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '0.75rem'
            }}>
              {conversationHistory[conversationHistory.length - 1].options.map((option, index) => (
                <Button
                  key={index}
                  color="primary"
                  outline
                  onClick={() => handleOptionClick(index)}
                  disabled={isLoading}
                  style={{
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    height: 'auto',
                    padding: '0.875rem 1.125rem',
                    wordBreak: 'break-word',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {option.text}
                </Button>
              ))}
            </div>
          </>
        )}

        {conversationHistory.length > 0 && 
         conversationHistory[conversationHistory.length - 1].isEndNode && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button color="success" onClick={handleRestart} style={{ flex: 1, minWidth: '150px' }}>
              <i className="fa fa-refresh mr-2"></i>
              {labels.newConsultation}
            </Button>
            <Button color="secondary" onClick={onClose} style={{ flex: 1, minWidth: '150px' }}>
              <i className="fa fa-times mr-2"></i>
              {labels.close}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  if (!isModal) {
    // Full page view
    if (hideHeader) {
      // Used within multi-tab view
      return (
        <div style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <ChatContent />
        </div>
      );
    }

    // Standalone page view
    return (
      <div className="technical-support-chat-page" style={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa'
      }}>
        <Card style={{
          height: '100%',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          borderRadius: 0
        }}>
          <CardHeader style={{ 
            backgroundColor: '#007bff',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <div>
              <h4 style={{ margin: 0 }}>
                <i className="fa fa-headphones mr-2"></i>
                {labels.technicalSupport}
              </h4>
              <small>{labels.autoAssistant}</small>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {machineId && (
                <Badge color="light" style={{ color: '#007bff' }}>
                  {labels.machine}: {machineId}
                </Badge>
              )}
              <Button 
                color="light" 
                size="sm" 
                onClick={onClose}
                style={{ marginLeft: '0.5rem' }}
              >
                <i className="fa fa-times"></i>
              </Button>
            </div>
          </CardHeader>
          <CardBody style={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <ChatContent />
          </CardBody>
        </Card>
      </div>
    );
  }

  // Modal view
  return (
    <div className="technical-support-chat-modal">
      <Card style={{ 
        margin: 0,
        height: '70vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <CardHeader style={{ 
          backgroundColor: '#007bff',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h5 style={{ margin: 0 }}>
              <i className="fa fa-headphones mr-2"></i>
              {labels.technicalSupport.replace(' VACWAY', '')}
            </h5>
            {machineId && (
              <small>{labels.machine}: {machineId}</small>
            )}
          </div>
          <Button close onClick={onClose} style={{ color: 'white', opacity: 1 }}>
            <span aria-hidden="true">×</span>
          </Button>
        </CardHeader>
        <CardBody style={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ChatContent />
        </CardBody>
      </Card>
    </div>
  );
};

export default TechnicalSupportChat;
